// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useTerminalStore from '@/app/store/modules/terminal';
import actionsPosTerminals from '../../../terminals/api/actionsPosTerminals';
import actionsRemoteCollections from '../../api/actionsRemoteCollections';
// Components
import DataTable from '../../../../../common-utils/components/renders/dataTable/DataTable';
// Common
import { columnsTransactions, columnsTransactionsId } from './columnDef/columnsTransactions';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import mergeQuery from '@/common-utils/components/renders/dataTable/utils/mergeQuery';
import transactionsFields from '../../fields/transactionsFields';
import { findApplicationFromCode } from '@/app/modules/contracts/enums/ItpRegex';

/**
 * @param root0
 * @param root0.posTerminalId
 * @param root0.remoteCollectionId
 * @param root0.setData
 * @param root0.customQuery
 * @param root0.rowsSelected
 * @param root0.setRowsSelected
 * @param root0.setIsLoading
 * @param root0.filtersEventName
 * @param root0.exportProps
 * @returns {JSX} TransactionsTable
 */
function TransactionsTable({
    posTerminalId = undefined,
    customQuery = undefined,
    rowsSelected = undefined,
    setRowsSelected = undefined,
    remoteCollectionId = undefined,
    setData = null,
    exportProps = null,
    setIsLoading,
    filtersEventName
}) {

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const {
        refetchTerminalTransactions: refetchFromStore,
        storeRefetchTerminalTransactions,
        queryTransactions: queryStored,
        storeQueryTransactions
    } = useTerminalStore();

    // State
    const initialQuery = getInitialQuery(customQuery);
    const [query, setQuery] = useState(mergeQuery(initialQuery, queryStored));
    const selectionMode = !!(rowsSelected && setRowsSelected);

    const [launchRefetch, setLaunchRefetch] = useState(true);

    // Api Calls
    const { data, isFetching, refetch } = remoteCollectionId && setData ?
        actionsRemoteCollections.getRemoteCollectionTransactions(query, remoteCollectionId, false)
        : actionsPosTerminals.getTransactions({ query: query, posTerminalId: posTerminalId }, false);

    // Hooks
    useEffect(() => {
        if (rootOrganization && (launchRefetch || refetchFromStore)) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchTerminalTransactions(false);
        }
    }, [query, launchRefetch, refetchFromStore]);

    useEffect(() => {
        if (data && !isFetching && remoteCollectionId && setData) {
            const { items = [], extraInformation } = data;

            const contractNumber = items
                .map(item => item?.[transactionsFields.CONTRACT.key])
                .find(value => value !== null)
                ?? null;

            const applicationCode = items
                .map(item => item?.[transactionsFields.APPLICATION.key])
                .find(value => value !== null);

            const application = applicationCode ? findApplicationFromCode(applicationCode) : null;

            setData({
                number: contractNumber,
                reconciliationNumber: extraInformation?.reconciliationNumber ?? null,
                application: application?.name ?? null
            });
        }
        if (setIsLoading) {
            setIsLoading(isFetching);
        }
    }, [data, isFetching]);

    const allColumns = columnsTransactions({ query, setQuery, queryStored, storeQueryTransactions, setLaunchRefetch, selectionMode, filtersEventName });

    // Render
    return (
        <DataTable
            filtersEventName={filtersEventName}
            columns={allColumns}
            data={data}
            dataFetchingIsLoading={isFetching}
            columnsId={columnsTransactionsId}
            initialQuery={initialQuery}
            query={query}
            setQuery={setQuery}
            queryStored={queryStored}
            storeQuery={storeQueryTransactions}
            setLaunchRefetch={setLaunchRefetch}

            selectionMode={selectionMode}
            rowsSelected={rowsSelected}
            setRowsSelected={setRowsSelected}

            exportProps={exportProps}
        />
    );
}

TransactionsTable.propTypes = {
    posTerminalId: PropTypes.string,
    remoteCollectionId: PropTypes.string,
    exportProps: PropTypes.object,
    setData: PropTypes.func,
    customQuery: PropTypes.object,
    rowsSelected: PropTypes.array,
    setRowsSelected: PropTypes.func,
    setIsLoading: PropTypes.func,
    filtersEventName: PropTypes.string
};

export default TransactionsTable;